import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";
import { isValidPhoneNumber } from "libphonenumber-js";
import { zodResolver } from "@hookform/resolvers/zod";
import { NumericFormat, PatternFormat } from "react-number-format";

import $ from "jquery";
import moment from "moment";
import MultiSelect from "../../../components/MultiSelect";

import {
  clearAll,
  showCertificateFormFunc,
} from "../../../../config/redux/slice/transfereeUserSlice";
import { appApi } from "../../../../utils/endpoints/appApi";
import { postRequest } from "../../../../helpers/request";
import {
  onAddCertificate,
  setErrorMessageTransfereeUser,
  setLoadingTransfereeUser,
} from "../../../../config/redux/slice/transfereeUserSlice";
import { Spinner } from "../../../components";
import { truncateMiddle } from "../../../../helpers/helpers";
import { getBankList } from "../../../../config/redux/actions/app";

import { document_list } from "../../../../constants/english";
import {
  CERTIFICATE_NUMBER_PREFIX,
  CERTIFICATE_NUMBER_REGEX,
  DATE_FORMAT_TEMPLATE,
} from "../../../../constants";
import { formatCertificateNumber } from "../../../../utils/lib";

import "bootstrap-select/dist/css/bootstrap-select.min.css";
import "bootstrap-select/dist/js/bootstrap-select.min.js";

const schema = z.object({
  amount_on_certificate: z.number().positive(),
  amount_requested: z.number().min(0),
  bank_id: z.number({ message: "*this field is required" }).positive(),
  certificate_date: z
    .string()
    .date("*this field is required")
    .refine(
      (value) => moment(value).isSameOrBefore(moment()),
      "Date must not be greater than today",
    ),
  certificate_file: z.instanceof(File, "*this field is required"),
  certificate_number: z
    .string()
    .regex(
      CERTIFICATE_NUMBER_REGEX,
      "Please use the correct format -> /AG/IC/000000000/00",
    ),
  certificate_number_two: z
    .number({
      required_error: "*this field is required",
      invalid_type_error: "Please enter only digits",
    })
    .positive()
    .transform((value) => CERTIFICATE_NUMBER_PREFIX + value),
  comment: z.string().optional(),
  contact_email: z.string().email(),
  contact_name: z.string().min(3, "Contact name must be at least 3 characters"),
  contact_phone: z
    .string({ required_error: "*this field is required" })
    .refine((value) => isValidPhoneNumber(value, "NG"), "Invalid phone number"),
  currency: z.string().min(3, "*this field is required"),
  effective_date: z.coerce.date(),
  file_names: z
    .array(
      z.object({
        label: z.string(),
        id: z.number(),
        name: z.string(),
        isRequired: z.boolean(),
        reference: z.string(),
      }),
    )
    .min(0)
    .default([]),
  expiry_date: z
    .string()
    .date("*this field is required")
    .max(moment().format(DATE_FORMAT_TEMPLATE))
    .refine(
      (value) => moment(value).isAfter(moment()),
      "Certificate is expired",
    ),
  stamped_agreement: z.instanceof(File, "*this field is required"),
});

const appendRefineToSchema = (validationSchema) => {
  return validationSchema
    .refine(
      (data) =>
        document_list
          .filter((document) => document.isRequired)
          .every((document) =>
            data.file_names.find((file) => file.label === document.name),
          ),
      {
        message: "Required additional files missing",
        path: ["file_names"],
      },
    )
    .refine((data) => moment(data.certificate_date).isBefore(moment()), {
      message: "Certificate must have been issued before today",
      path: ["certificate_date"],
    })
    .refine(
      (data) => moment(data.expiry_date).isAfter(moment(data.certificate_date)),
      {
        message: "Certificate expiry date must be after the issued date",
        path: ["expiry_date"],
      },
    )
    .refine(
      (data) =>
        parseFloat(data.amount_requested) <=
        parseFloat(data.amount_on_certificate),
      {
        message: "Amount greater than amount on certificate",
        path: ["amount_requested"],
      },
    );
};

function CertificateUploadForm() {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.authData);
  const { currencies, banks } = useSelector((state) => state.app);
  const { transfereeUserLoading, transfereeUserErrorMsg } = useSelector(
    (state) => state.transfereeUser,
  );

  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState("");
  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [issueDate, setIssueDate] = useState(null);
  const [bankMismatchError, setBankMismatchError] = useState("");
  const [isValidatingBankRep, setIsValidatingBankRep] = useState(false);

  const validateBankRep = useCallback(
    async (email, bankId) => {
      if (!email || !bankId) {
        setBankMismatchError("");
        return true;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return true;
      }

      setIsValidatingBankRep(true);
      try {
        const res = await postRequest(
          token,
          `${appApi.validateBankRep}`,
          { contact_email: email, bank_id: bankId },
          dispatch,
          "",
          "",
          "",
        );

        if (res && res.status >= 400) {
          const msg =
            res.data?.message ||
            "The selected bank does not match the bank representative's registered bank.";
          setBankMismatchError(msg);
          return false;
        } else {
          setBankMismatchError("");
          return true;
        }
      } catch (err) {
        return true;
      } finally {
        setIsValidatingBankRep(false);
      }
    },
    [token, dispatch],
  );
  const extendedSchema = useMemo(() => {
    if (additionalDocuments.length === 0) return appendRefineToSchema(schema);

    const additionalSchema = {};

    additionalDocuments.forEach((document) => {
      if (document.isRequired)
        additionalSchema[document.reference] = z.instanceof(
          File,
          "*this field is required",
        );
      else
        additionalSchema[document.reference] = z
          .instanceof(File, `*Upload a valid ${document.label} file`)
          .optional();
    });

    return appendRefineToSchema(schema.extend(additionalSchema));
  }, [additionalDocuments]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(extendedSchema),
    mode: "all",
  });



  const closeModal = useCallback(() => {
    dispatch(clearAll({}));
    dispatch(showCertificateFormFunc({ modal: false }));
  }, [dispatch]);

  const onSubmit = useCallback(
    async (data) => {
      dispatch(clearAll({}));

      if (data.contact_email && data.bank_id) {
        const isValidRep = await validateBankRep(data.contact_email, data.bank_id);
        if (!isValidRep) {
          return;
        }
      }

      const apiData = {
        ...data,
        issuer: "NOTAP",
        file_names: data.file_names.map((file) => file.name).join(","),
      };
      const formData = new FormData();

      for (const key in apiData) {
        if (key !== "file_names") {
          formData.append(key, apiData[key]);
        }
      }

      formData.append(
        "file_names",
        data.file_names.map((doc) => doc.reference).join(","),
      );

      data.file_names.forEach((doc) => {
        const file = data[doc.reference];
        if (file instanceof File) {
          formData.append("documents", file);
        }
      });

      await postRequest(
        token,
        `${appApi.uploadCertificate}`,
        formData,
        dispatch,
        setLoadingTransfereeUser,
        onAddCertificate,
        setErrorMessageTransfereeUser,
        true,
      );
    },
    [dispatch, token],
  );

  useEffect(() => {
    dispatch(getBankList());
  }, [dispatch]);

  useEffect(() => {
    function initializePicker() {
      $(function () {
        $(".my-select").selectpicker();
      });
    }

    initializePicker();
  }, []);

  useEffect(() => {
    const { unsubscribe } = watch((value, { name }) => {
      if (name === "currency" && value.currency) {
        const currencyMatch = currencies.find(
          (currency) => value.currency === currency.name,
        );
        if (currencyMatch) setSelectedCurrencySymbol(currencyMatch.symbol);
      }

      if (name === "certificate_date" && value.certificate_date) {
        setIssueDate(value.certificate_date);
      }

      if (name === "file_names" && value.file_names) {
        setAdditionalDocuments(value.file_names);
      }

      if (name === "contact_email" || name === "bank_id") {
        if (value.contact_email && value.bank_id) {
          validateBankRep(value.contact_email, value.bank_id);
        } else {
          setBankMismatchError("");
        }
      }
    });

    return () => unsubscribe();
  }, [watch, currencies]);

  return (
    <div
      className="modal fade show d-block overflow-y-auto"
      id="basicModal"
      tabIndex="-1"
      aria-labelledby="exampleModalCenterTitle"
      aria-hidden="true"
      style={{ backgroundColor: "rgba(0,0,0,0.3)", overflowY: "auto" }}
    >
      <div
        style={{ overflowY: "auto" }}
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <form
            onSubmit={handleSubmit(onSubmit)}
            autoComplete={"off"}
            encType="multipart/form-data"
            noValidate
          >
            <div className="modal-header px-4">
              <h6 className="modal-title" id="exampleModalCenterTitle">
                Upload Certificate
              </h6>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={closeModal}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            <div className="modal-body px-4 px-md-7">
              <div>
                <h6 className="mb-3 font-weight-bold">
                  Certificate Information
                </h6>
                <div className="row mb-2">
                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="certificate_number">
                        Reference ID (File No)
                      </label>

                      <Controller
                        control={control}
                        name="certificate_number"
                        render={(props) => (
                          <input
                            type="text"
                            className="form-control input-lg"
                            id="certificate_number"
                            placeholder="e.g. AG/IC/000000000/00"
                            value={props.field.value}
                            onChange={props.field.onChange}
                            onKeyUp={(event) => {
                              const inputValue = event.target.value;
                              const formattedNumber =
                                event.key === "/"
                                  ? inputValue
                                  : formatCertificateNumber(inputValue);

                              props.field.onChange(formattedNumber);
                            }}
                          />
                        )}
                      />

                      {errors.certificate_number && (
                        <div className="text-danger small mt-1">
                          {errors.certificate_number.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="certificate_number_two">
                        Certificate Number
                      </label>

                      <Controller
                        control={control}
                        name="certificate_number_two"
                        render={(props) => (
                          <NumericFormat
                            allowLeadingZeros
                            className="form-control input-lg"
                            prefix={CERTIFICATE_NUMBER_PREFIX}
                            value={props.field.value}
                            onValueChange={(values) =>
                              props.field.onChange(values.floatValue)
                            }
                          />
                        )}
                      />
                      {errors.certificate_number_two && (
                        <div className="text-danger small mt-1">
                          {errors.certificate_number_two.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="certificate_date">
                        Certificate Issue Date
                      </label>
                      <input
                        type="date"
                        max={moment().format(DATE_FORMAT_TEMPLATE)}
                        className="form-control input-lg"
                        id="certificate_date"
                        {...register("certificate_date")}
                      />
                      {errors.certificate_date && (
                        <div className="text-danger small mt-1">
                          {errors.certificate_date.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="expiry_date">
                        Certificate Expiry Date
                      </label>
                      <input
                        type="date"
                        min={issueDate ?? null}
                        className="form-control input-lg"
                        id="expiry_date"
                        {...register("expiry_date")}
                      />
                      {errors.expiry_date && (
                        <div className="text-danger small mt-1">
                          {errors.expiry_date.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="effective_date">
                        Certificate Effective Date
                      </label>
                      <input
                        type="date"
                        className="form-control input-lg"
                        id="effective_date"
                        {...register("effective_date")}
                      />
                      {errors.effective_date && (
                        <div className="text-danger small mt-1">
                          {errors.effective_date.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="currency">Currency</label>
                      <select
                        className="form-control rounded-0 selectpicker my-select"
                        id="currency"
                        data-live-search="true"
                        {...register("currency")}
                      >
                        <option value={""}>Select Currency</option>
                        {currencies.map((currencyItem, i) => (
                          <option key={i} value={currencyItem.name}>
                            {currencyItem.name}
                          </option>
                        ))}
                      </select>
                      {errors.currency && (
                        <div className="text-danger small mt-1">
                          {errors.currency.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="amount_on_certificate">
                        Amount On Certificate
                      </label>

                      <Controller
                        control={control}
                        name="amount_on_certificate"
                        render={(props) => (
                          <NumericFormat
                            allowLeadingZeros={false}
                            className="form-control input-lg"
                            thousandSeparator=","
                            decimalSeparator="."
                            prefix={`${selectedCurrencySymbol} `}
                            value={props.field.value}
                            onValueChange={(values) =>
                              props.field.onChange(values.floatValue)
                            }
                          />
                        )}
                      />

                      {errors.amount_on_certificate && (
                        <div className="text-danger small mt-1">
                          {errors.amount_on_certificate.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="amount_requested">Amount Requested</label>

                      <Controller
                        control={control}
                        name="amount_requested"
                        render={(props) => (
                          <NumericFormat
                            allowLeadingZeros={false}
                            className="form-control input-lg"
                            thousandSeparator=","
                            decimalSeparator="."
                            prefix={`${selectedCurrencySymbol} `}
                            value={props.field.value}
                            onValueChange={(values) =>
                              props.field.onChange(values.floatValue)
                            }
                          />
                        )}
                      />

                      {errors.amount_requested && (
                        <div className="text-danger small mt-1">
                          {errors.amount_requested.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h6 className="mb-3 font-weight-bold">Bank Information</h6>
                <div className="row mb-2">
                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="bank_id">Select your Bank</label>
                      <select
                        className={`form-control rounded-0 ${bankMismatchError ? "is-invalid border-danger" : ""}`}
                        id="bank_id"
                        {...register("bank_id", {
                          valueAsNumber: true,
                          onChange: (e) => {
                            const email = watch("contact_email");
                            validateBankRep(email, e.target.value);
                          },
                        })}
                      >
                        <option value={""}>Select Bank</option>
                        {banks.map((bank) => (
                          <option key={bank.ID} value={bank.ID}>
                            {bank.BANK_NAME}
                          </option>
                        ))}
                      </select>
                      {errors.bank_id && (
                        <div className="text-danger small mt-1">
                          {errors.bank_id.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="contact_name">Contact Name</label>
                      <input
                        type="text"
                        className="form-control input-lg"
                        id="contact_name"
                        {...register("contact_name")}
                      />
                      {errors.contact_name && (
                        <div className="text-danger small mt-1">
                          {errors.contact_name.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="contact_email">Contact Email</label>
                      <input
                        type="email"
                        className={`form-control input-lg ${bankMismatchError ? "is-invalid border-danger" : ""}`}
                        id="contact_email"
                        {...register("contact_email", {
                          onBlur: (e) => {
                            const bankId = watch("bank_id");
                            validateBankRep(e.target.value, bankId);
                          },
                        })}
                      />
                      {isValidatingBankRep && (
                        <div className="text-muted small mt-1">
                          <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                          Verifying bank representative...
                        </div>
                      )}
                      {errors.contact_email && (
                        <div className="text-danger small mt-1">
                          {errors.contact_email.message}
                        </div>
                      )}
                      {!errors.contact_email && bankMismatchError && (
                        <div className="text-danger small mt-1 font-weight-bold">
                          {bankMismatchError}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="contact_phone">
                        Contact Phone Number
                      </label>

                      <Controller
                        control={control}
                        name="contact_phone"
                        render={(props) => (
                          <PatternFormat
                            className="form-control input-lg"
                            format="+234 ### ### ####"
                            value={props.field.value}
                            onValueChange={(values) =>
                              props.field.onChange(values.formattedValue)
                            }
                          />
                        )}
                      />

                      {errors.contact_phone && (
                        <div className="text-danger small mt-1">
                          {errors.contact_phone.message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h6 className="mb-3 font-weight-bold">Documents Upload</h6>
                <div className="row mt-4">
                  <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="certificate_file">Certificate</label>
                      <div>
                        <div className="custom-file mb-1">
                          <Controller
                            control={control}
                            name="certificate_file"
                            render={(props) => (
                              <>
                                <input
                                  type="file"
                                  className="custom-file-input"
                                  id="certificate_file"
                                  onChange={(event) =>
                                    props.field.onChange(
                                      event.target.files?.[0],
                                    )
                                  }
                                />

                                <label
                                  id="custom-certificate"
                                  className="custom-file-label"
                                >
                                  {props.field.value?.name
                                    ? truncateMiddle(props.field.value.name, 30)
                                    : "Choose file..."}
                                </label>
                              </>
                            )}
                          />

                          {errors.certificate_file && (
                            <div className="text-danger small mt-1">
                              {errors.certificate_file.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div className="form-group">
                      <div>
                        <label className="mr-2" htmlFor="stamped_agreement">
                          Stamped Agreement
                        </label>
                      </div>
                      <div>
                        <div className="custom-file mb-1">
                          <Controller
                            control={control}
                            name="stamped_agreement"
                            render={(props) => (
                              <>
                                <input
                                  type="file"
                                  className="custom-file-input"
                                  id="stamped_agreement"
                                  onChange={(event) =>
                                    props.field.onChange(
                                      event.target.files?.[0],
                                    )
                                  }
                                />

                                <label
                                  id="custom-certificate"
                                  className="custom-file-label"
                                >
                                  {props.field.value?.name
                                    ? truncateMiddle(props.field.value.name, 30)
                                    : "Choose file..."}
                                </label>
                              </>
                            )}
                          />

                          {errors.stamped_agreement && (
                            <div className="text-danger small mt-1">
                              {errors.stamped_agreement.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* <div className="col-lg-6">
                    <div className="form-group">
                      <label htmlFor="cover_letter">Cover Letter</label>
                      <div>
                        <div className="custom-file mb-1">
                          <Controller
                            control={control}
                            name="cover_letter"
                            render={(props) => (
                              <>
                                <input
                                  type="file"
                                  className="custom-file-input"
                                  id="cover_letter"
                                  onChange={(event) =>
                                    props.field.onChange(
                                      event.target.files?.[0],
                                    )
                                  }
                                />

                                <label
                                  id="custom-certificate"
                                  className="custom-file-label"
                                >
                                  {props.field.value?.name
                                    ? truncateMiddle(props.field.value.name, 30)
                                    : "Choose file..."}
                                </label>
                              </>
                            )}
                          />
                          {errors.cover_letter && (
                            <div className="text-danger small mt-1">
                              {errors.cover_letter.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div> */}

                  <div className="col-lg-6">
                    <Controller
                      control={control}
                      name="file_names"
                      render={(props) => (
                        <MultiSelect
                          options={document_list.map((document) => ({
                            label: document.name,
                            value: document.id,
                            ...document,
                          }))}
                          closeMenuOnSelect={false}
                          isMulti
                          label="Select Additional Document To Upload"
                          isSearchable
                          name="file_names"
                          placeholder="Select document"
                          selectedValues={props.field.value}
                          onSelect={props.field.onChange}
                        />
                      )}
                    />

                    {errors.file_names && (
                      <div className="text-danger small mt-1">
                        {errors.file_names.message}
                      </div>
                    )}
                  </div>

                  <>
                    {additionalDocuments.map((document) => (
                      <div key={document.id} className="col-lg-6">
                        <div className="form-group">
                          <label className="mr-2" htmlFor={document.reference}>
                            {document.name}
                          </label>
                          {document.isRequired ? null : (
                            <button
                              type="button"
                              onClick={() =>
                                setValue(
                                  "file_names",
                                  additionalDocuments.filter(
                                    (item) => document.name !== item.name,
                                  ),
                                )
                              }
                              className="btn btn-sm text-white bg-danger"
                            >
                              <span className="mr-1" aria-hidden="true">
                                ×
                              </span>
                              Remove
                            </button>
                          )}

                          <div>
                            <div className="custom-file mb-1">
                              <Controller
                                control={control}
                                name={document.reference}
                                render={(props) => (
                                  <>
                                    <input
                                      type="file"
                                      className="custom-file-input"
                                      onChange={(event) =>
                                        props.field.onChange(
                                          event.target.files?.[0],
                                        )
                                      }
                                    />

                                    <label
                                      id="custom-certificate"
                                      className="custom-file-label"
                                    >
                                      {props.field.value?.name
                                        ? truncateMiddle(
                                            props.field.value.name,
                                            30,
                                          )
                                        : "Choose file..."}
                                    </label>
                                  </>
                                )}
                              />

                              {errors[document.reference] && (
                                <div className="text-danger small mt-1">
                                  {errors[document.reference].message}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="comment">Comment</label>
                <textarea
                  style={{ resize: "none" }}
                  className="form-control"
                  id="comment"
                  rows="4"
                  {...register("comment")}
                ></textarea>
                {errors.comment && (
                  <div className="text-danger small mt-1">
                    {errors.comment.message}
                  </div>
                )}
              </div>
            </div>

            {transfereeUserErrorMsg && (
              <div className="px-4 px-md-7 mb-4">
                <div className="alert alert-danger" role="alert">
                  {transfereeUserErrorMsg}
                </div>
              </div>
            )}

            <div className="modal-footer px-4 d-flex align-items-center">
              <button
                disabled={transfereeUserLoading ? true : false}
                onClick={() => {
                  reset();
                }}
                type="button"
                className="btn btn-smoke"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <div>
                {transfereeUserLoading ? (
                  <Spinner />
                ) : (
                  <button type="submit" className="btn btn-primary">
                    Send link to bank contact
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CertificateUploadForm;
